import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHead } from "@/pages/admin/AdminLayout";
import { api, WEEKDAYS, formatApiError } from "@/lib/api";

const EMPTY = { name: "", title: "", bio: "", image: "", availability: {}, service_ids: [], active: true };

const WeekEditor = ({ value, onChange }) => (
  <div className="space-y-2" data-testid="availability-editor">
    {WEEKDAYS.map((label, i) => {
      const k = String(i);
      const win = value[k]?.[0];
      const setWin = (w) => onChange({ ...value, [k]: w ? [w, ...(value[k] || []).slice(1)] : [] });
      return (
        <div key={k} className="flex items-center gap-3">
          <Switch checked={!!win} onCheckedChange={(on) => setWin(on ? { start: "09:00", end: "17:00" } : null)} data-testid={`availability-day-switch-${i}`} />
          <span className="w-10 text-sm font-medium">{label}</span>
          {win ? (
            <>
              <Input type="time" className="w-28 h-9" value={win.start} onChange={(e) => setWin({ ...win, start: e.target.value })} data-testid={`availability-start-${i}`} />
              <span className="text-muted-foreground text-sm">to</span>
              <Input type="time" className="w-28 h-9" value={win.end} onChange={(e) => setWin({ ...win, end: e.target.value })} data-testid={`availability-end-${i}`} />
            </>
          ) : <span className="text-sm text-muted-foreground">Not working</span>}
        </div>
      );
    })}
  </div>
);

const PracDialog = ({ open, onClose, initial, services, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [initial, open]);
  const toggleSvc = (id) => setF({ ...f, service_ids: f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id] });
  const save = async () => {
    const body = { name: f.name, title: f.title, bio: f.bio, image: f.image, availability: f.availability, service_ids: f.service_ids, active: f.active };
    try {
      if (initial?.id) await api.put(`/admin/practitioners/${initial.id}`, body); else await api.post("/admin/practitioners", body);
      toast.success("Practitioner saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="practitioner-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit practitioner" : "New practitioner"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="practitioner-name-input" /></div>
          <div className="space-y-1.5"><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="practitioner-title-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Photo URL</Label><Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="https://…" data-testid="practitioner-image-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Bio</Label><Textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="practitioner-bio-input" /></div>
        </div>
        <div className="space-y-2">
          <Label>Treatments offered</Label>
          <div className="grid sm:grid-cols-2 gap-2">
            {services.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm">
                <Checkbox checked={f.service_ids.includes(s.id)} onCheckedChange={() => toggleSvc(s.id)} data-testid={`practitioner-service-checkbox-${s.id}`} />{s.name}
              </label>
            ))}
          </div>
        </div>
        <div className="space-y-2"><Label>Weekly availability</Label><WeekEditor value={f.availability} onChange={(a) => setF({ ...f, availability: a })} /></div>
        <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="practitioner-active-switch" />Accepting bookings</label>
        <DialogFooter><Button onClick={save} disabled={!f.name} data-testid="practitioner-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const hoursSummary = (av) => WEEKDAYS.filter((_, i) => av?.[String(i)]?.length).map((d, i) => d).join(", ") || "No hours set";

export default function Practitioners() {
  const [list, setList] = useState([]);
  const [services, setServices] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/practitioners").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/admin/services").then((r) => setServices(r.data)); }, []);
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name}?`)) return;
    try { await api.delete(`/admin/practitioners/${p.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fade-up">
      <PageHead eyebrow="Team" title="Practitioners">
        <Button className="rounded-full" onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-practitioner-button"><Plus className="h-4 w-4 mr-1.5" />Add practitioner</Button>
      </PageHead>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((p) => (
          <div key={p.id} className="rounded-2xl bg-card border border-border p-5" data-testid={`admin-practitioner-card-${p.id}`}>
            <div className="flex items-center gap-3">
              {p.image ? <img src={p.image} alt={p.name} className="h-14 w-14 rounded-xl object-cover" /> : <div className="h-14 w-14 rounded-xl bg-muted" />}
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold truncate">{p.name}</p>
                <p className="text-sm text-primary truncate">{p.title}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => { setEdit(p); setOpen(true); }} data-testid={`edit-practitioner-button-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`delete-practitioner-button-${p.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">Works: {hoursSummary(p.availability)}</p>
            <p className="text-xs text-muted-foreground">{p.service_ids.length} treatments · {p.active ? "Accepting bookings" : "Paused"}</p>
          </div>
        ))}
      </div>
      <PracDialog open={open} onClose={() => setOpen(false)} initial={edit} services={services} onSaved={load} />
    </div>
  );
}
